import { FieldPath } from "react-hook-form";
import { OcrField } from "../../backend/taxguru/models/OcrField";
import { findMomentForVatRates } from "./function/documentUtils";
import { DocumentDetailFormTypes } from "./detail/DocumentDetail.types";
import { VatRatesType } from "./function/hook/useVatPayer";
import { paymentMeanToNumber } from "./function/recogniserUtils";

export class TaxGuruConditionalFieldCode {
    public readonly taxFieldCode: FieldPath<DocumentDetailFormTypes>;
    public readonly matches: (ocrField: OcrField) => boolean;
    public readonly ocrFieldUpdateObject: Partial<OcrField>;
    public valueFromForm: ((data: DocumentDetailFormTypes) => DocumentDetailFormTypes[keyof DocumentDetailFormTypes]) | undefined;

    constructor(
        taxFieldCode: FieldPath<DocumentDetailFormTypes>,
        condition?: (ocrField: OcrField) => boolean,
        ocrFieldUpdateObject?: Partial<OcrField>
    ) {
        this.taxFieldCode = taxFieldCode;
        this.matches = condition ? condition : () => true;
        this.ocrFieldUpdateObject = ocrFieldUpdateObject ? ocrFieldUpdateObject : {};
    }

    public withValueFromForm(valueFromForm: typeof this.valueFromForm) {
        this.valueFromForm = valueFromForm;
        return this;
    }
}

export class TaxGuruTaxRateFieldCode extends TaxGuruConditionalFieldCode {
    public readonly taxRateIndex: number;

    constructor(taxFieldCode: FieldPath<DocumentDetailFormTypes>, taxRateIndex: number) {
        super(taxFieldCode, (ocrField) => ocrField.taxrateIndex === taxRateIndex, { taxrateIndex: taxRateIndex });
        this.taxRateIndex = taxRateIndex;
    }
}

export type TaxGuruFieldType = TaxGuruConditionalFieldCode;
export type RecogniserMapping = {
    recogniserFieldCode: string;
    taxGuruFieldCodes: Array<TaxGuruFieldType>;
};
export type RecogniserSingleMapping = {
    recogniserFieldCode: string;
    taxGuruFieldCode: TaxGuruFieldType;
};

export const createRecogniserMappings = (vatRates: VatRatesType | undefined): Array<RecogniserMapping> => {
    const { vat0, vatBasic, vatReduced, vatReduced2 } = vatRates ?? {};
    return [
        { recogniserFieldCode: "InvoiceId", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("regNumber")] },
        { recogniserFieldCode: "DocumentType", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("type")] },
        { recogniserFieldCode: "Currency", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("currency")] },
        { recogniserFieldCode: "InvoiceTotal", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("amountTotal")] },
        { recogniserFieldCode: "InvoiceDate", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("issueDate")] },
        { recogniserFieldCode: "DueDate", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("dueDate")] },
        { recogniserFieldCode: "AmountDue", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("payableAmount")] },
        { recogniserFieldCode: "Iban", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("iban")] },
        { recogniserFieldCode: "AccountNumber", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("accountNumber")] },
        { recogniserFieldCode: "BankCode", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("bankCode")] },
        { recogniserFieldCode: "VariableSymbol", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("variableSymbol")] },
        { recogniserFieldCode: "Bic", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("bic")] },
        {
            recogniserFieldCode: "PaymentType",
            taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("paymentMeans").withValueFromForm(paymentMeanToNumber)],
        },

        // tax rates
        { recogniserFieldCode: "SubTotal", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("baseTotal")] },
        { recogniserFieldCode: "TotalTax", taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("vatTotal")] },
        {
            recogniserFieldCode: "TaxPointDate",
            taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("taxableSupplyDate").withValueFromForm(findMomentForVatRates)],
        },
        {
            recogniserFieldCode: "TaxRateSubTotal",
            taxGuruFieldCodes: [
                ...(vat0?.rate === 0 ? [new TaxGuruTaxRateFieldCode("baseVat0", vat0.rate)] : []),
                ...(vatBasic?.rate ? [new TaxGuruTaxRateFieldCode("baseVatBasic", vatBasic.rate)] : []),
                ...(vatReduced?.rate ? [new TaxGuruTaxRateFieldCode("baseVatReduced", vatReduced.rate)] : []),
                ...(vatReduced2?.rate ? [new TaxGuruTaxRateFieldCode("baseVatReduced2", vatReduced2.rate)] : []),
            ],
        },
        {
            recogniserFieldCode: "TaxRateTaxAmount",
            taxGuruFieldCodes: [
                ...(vat0?.rate === 0 ? [new TaxGuruTaxRateFieldCode("vat0", vat0.rate)] : []),
                ...(vatBasic?.rate ? [new TaxGuruTaxRateFieldCode("vatBasic", vatBasic.rate)] : []),
                ...(vatReduced?.rate ? [new TaxGuruTaxRateFieldCode("vatReduced", vatReduced.rate)] : []),
                ...(vatReduced2?.rate ? [new TaxGuruTaxRateFieldCode("vatReduced2", vatReduced2.rate)] : []),
            ],
        },
        {
            recogniserFieldCode: "TaxRateAmount",
            taxGuruFieldCodes: [
                ...(vat0?.rate === 0 ? [new TaxGuruTaxRateFieldCode("amountVat0", vat0.rate)] : []),
                ...(vatBasic?.rate ? [new TaxGuruTaxRateFieldCode("amountVatBasic", vatBasic.rate)] : []),
                ...(vatReduced?.rate ? [new TaxGuruTaxRateFieldCode("amountVatReduced", vatReduced.rate)] : []),
                ...(vatReduced2?.rate ? [new TaxGuruTaxRateFieldCode("amountVatReduced2", vatReduced2.rate)] : []),
            ],
        },
        {
            recogniserFieldCode: "TaxRatePercentage",
            taxGuruFieldCodes: [
                ...(vat0?.rate === 0 ? [new TaxGuruTaxRateFieldCode("vatRate0", vat0.rate)] : []),
                ...(vatBasic?.rate ? [new TaxGuruTaxRateFieldCode("vatRateBasic", vatBasic.rate)] : []),
                ...(vatReduced?.rate ? [new TaxGuruTaxRateFieldCode("vatRateReduced", vatReduced.rate)] : []),
                ...(vatReduced2?.rate ? [new TaxGuruTaxRateFieldCode("vatRateReduced2", vatReduced2.rate)] : []),
            ],
        },

        // supplier
        {
            recogniserFieldCode: "SupplierCompanyId",
            taxGuruFieldCodes: [
                new TaxGuruConditionalFieldCode("supplier"),
                new TaxGuruConditionalFieldCode("supplierPerson"),
                new TaxGuruConditionalFieldCode("supplierCompanyIdNumber"),
            ],
        },
        {
            recogniserFieldCode: "SupplierTaxId",
            taxGuruFieldCodes: [
                new TaxGuruConditionalFieldCode("supplier"),
                new TaxGuruConditionalFieldCode("supplierPerson"),
                new TaxGuruConditionalFieldCode("supplierTaxIdNumber"),
            ],
        },
        {
            recogniserFieldCode: "SupplierName",
            taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("supplier"), new TaxGuruConditionalFieldCode("supplierPerson")],
        },
        // customer
        {
            recogniserFieldCode: "CustomerCompanyId",
            taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("customerCompanyIdNumber")],
        },
        {
            recogniserFieldCode: "CustomerTaxId",
            taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("customerCompanyIdNumber")],
        },
        {
            recogniserFieldCode: "CustomerName",
            taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("customerCompanyIdNumber")],
        },
        {
            recogniserFieldCode: "CustomerAccountingAddress",
            taxGuruFieldCodes: [new TaxGuruConditionalFieldCode("customerCompanyIdNumber")],
        },
    ];
};

export const findMappingByRecogniserFieldCode = (mappings: Array<RecogniserMapping>, recogniserFieldCode: string): RecogniserMapping | undefined => {
    return mappings.find((mapping) => mapping.recogniserFieldCode === recogniserFieldCode);
};

export const findMappingByTaxFieldCode = (
    mappings: Array<RecogniserMapping>,
    taxFieldCode: FieldPath<DocumentDetailFormTypes>
): RecogniserSingleMapping | undefined => {
    const mapping = mappings.find((mapping) => {
        return mapping.taxGuruFieldCodes.some((item) => {
            return item.taxFieldCode === taxFieldCode;
        });
    });
    const oneMapping = mapping?.taxGuruFieldCodes?.find((item) => item.taxFieldCode === taxFieldCode);
    if (mapping && oneMapping) {
        return {
            recogniserFieldCode: mapping.recogniserFieldCode,
            taxGuruFieldCode: oneMapping,
        };
    }
};
